import { FileDTO } from '@app-libs/model';
import { 
  getSimplifiedFileType, 
} from '@app-libs/core';

export interface FileValidationOptions {
  maxSizeMB?: number;
  allowedTypes?: string[];
}

export interface FileUploadMetadata {
  fileName: string;
  fileType: string;
  fileMime: string;
  fileSize: number;
  filePath?: string;
}

/**
 * Default allowed file types for upload
 */
export const DEFAULT_ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/text',
  'application/msword',
  'text/plain',
];

/**
 * Default maximum file size in MB
 */
export const DEFAULT_MAX_FILE_SIZE_MB = 10;

/**
 * Validates if a file type is allowed
 * @param file - The file to validate
 * @param allowedTypes - Array of allowed MIME types
 * @returns True if file type is allowed
 */
export function isValidFileType(file: File, allowedTypes: string[] = DEFAULT_ALLOWED_FILE_TYPES): boolean {
  return allowedTypes.includes(file.type);
}

/**
 * Validates if a file size is within limits
 * @param file - The file to validate
 * @param maxSizeMB - Maximum allowed size in MB
 * @returns True if file size is valid
 */
export function isValidFileSize(file: File, maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB): boolean {
  const fileSizeMB = file.size / 1024 / 1024;
  return fileSizeMB <= maxSizeMB;
}

/**
 * Gets the file size in MB
 * @param file - The file to measure
 * @returns File size in MB
 */
export function getFileSizeMB(file: File): number {
  return file.size / 1024 / 1024;
}

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Checks if a file is an image based on MIME type
 * @param file - The file to check
 * @returns True if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Checks if a file is a PDF based on MIME type
 * @param file - The file to check
 * @returns True if file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/**
 * Extracts file metadata from a File object
 * @param file - The file to extract metadata from
 * @returns File metadata object
 */
export function extractFileMetadata(file: File): FileUploadMetadata {
  return {
    fileName: file.name,
    fileType: getSimplifiedFileType(file.type),
    fileMime: file.type,
    fileSize: file.size,
    filePath: file.webkitRelativePath || file.name
  };
}

/**
 * Gets a simplified file type from MIME type
 * @param mimeType - The MIME type
 * @returns Simplified file type (pdf, image, etc.)
 * @deprecated Use getSimplifiedFileType from @app-libs/core instead
 */
export function getFileTypeFromMime(mimeType: string): string {
  return getSimplifiedFileType(mimeType);
}

/**
 * Creates a FileDTO object from uploaded data and file
 * @param uploaded - Partial file data from processing
 * @param file - Original file object
 * @param userId - User ID
 * @returns Complete FileDTO object
 */
export function createFileUploadObject(
  uploaded: Partial<FileDTO>,
  file: File,
  userId: string
): FileDTO {
  const fileHandle = uploaded.file_handle || file;
  const metadata = extractFileMetadata(fileHandle);
  
  return {
    file_id: undefined,
    user_id: userId,
    type: uploaded.type || metadata.fileType,
    file_name: metadata.fileName,
    file_mime: metadata.fileMime,
    file_path: metadata.filePath || '',
    file_size: metadata.fileSize,
    content: uploaded.content,
    url: uploaded.url,
    uploaded_at: new Date(),
    file_handle: fileHandle,
  } as unknown as FileDTO;
}

/**
 * Converts a File to base64 string
 * @param file - The file to convert
 * @returns Promise resolving to base64 string
 */
export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * Converts an ArrayBuffer to base64 string
 * @param buffer - The array buffer to convert
 * @returns Base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Gets file extension from filename
 * @param filename - The filename
 * @returns File extension (without dot) or empty string
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
}

/**
 * Validates if a file can be processed based on type and size
 * @param file - The file to validate
 * @param options - Validation options
 * @returns Object with valid flag and error message if invalid
 */
export function validateFile(
  file: File,
  options: FileValidationOptions = {}
): { valid: boolean; error?: string } {
  const { maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB, allowedTypes = DEFAULT_ALLOWED_FILE_TYPES } = options;
  
  if (!isValidFileType(file, allowedTypes)) {
    return { valid: false, error: `File type ${file.type} is not supported` };
  }
  
  if (!isValidFileSize(file, maxSizeMB)) {
    return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit` };
  }
  
  return { valid: true };
}

/**
 * Formats a filename for display with truncation
 * @param filename - The filename to format
 * @param maxLength - Maximum length before truncation
 * @returns Formatted filename
 */
export function formatDisplayFilename(filename: string, maxLength = 40): string {
  if (!filename) return '';
  
  if (filename.length <= maxLength) {
    return filename;
  }
  
  const extension = getFileExtension(filename);
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  const truncateLength = maxLength - extension.length - 4; // 4 for "..." and "."
  
  if (truncateLength <= 0) {
    return filename.substring(0, maxLength - 3) + '...';
  }
  
  return `${nameWithoutExt.substring(0, truncateLength)}...${extension ? '.' + extension : ''}`;
}

/**
 * Checks if a media recorder format is supported
 * @param mimeType - The MIME type to check
 * @returns True if the format is supported
 */
export function isMediaRecorderFormatSupported(mimeType: string): boolean {
  return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType);
}

/**
 * Gets the appropriate audio recording format
 * @param preferredFormat - Preferred format (e.g., 'webm', 'mp4')
 * @returns Supported MIME type for audio recording
 */
export function getAudioRecordingFormat(preferredFormat = 'webm'): string {
  const mimeType = `audio/${preferredFormat}`;
  
  if (isMediaRecorderFormatSupported(mimeType)) {
    return mimeType;
  }
  
  // Fallback to webm if preferred format not supported
  if (preferredFormat !== 'webm' && isMediaRecorderFormatSupported('audio/webm')) {
    return 'audio/webm';
  }
  
  // Try other common formats
  const formats = ['audio/mp4', 'audio/ogg', 'audio/wav'];
  for (const format of formats) {
    if (isMediaRecorderFormatSupported(format)) {
      return format;
    }
  }
  
  // Return webm as last resort (browser will handle if not supported)
  return 'audio/webm';
}